import { CoreApi, ProductType } from '@inter-ikea-storageone/range';
import { SelectedMenu, SelectedMenuProductList } from 'rangeConfig/@types';
import deleteSelectedEntity from 'rangeConfig/shared/behaviors/deleteSelectedEntity';
import replaceProduct from 'rangeConfig/shared/behaviors/replaceProduct';
import {
  matchDimensions,
  matchFrameFrontType
} from 'rangeConfig/shared/filters/selectedMenuProductFilters';
import platform from 'rangeConfig/shared/utils/platform';
import TopPanelWidgetSelectedMenu from 'rangeConfig/shared/widgets/topPanels/TopPanelWidgetSelectedMenu';
import {
  hideChangingTableFunctionWidget,
  hideFrameDrawerWidget,
  hideTopPanelsMenu
} from 'rangeConfig/smastad/rules/hideMenuRules';
import hideDoors from 'rangeConfig/shared/behaviors/hideDoors';
import addToTarget from 'rangeConfig/shared/behaviors/addToTarget';
import {
  matchAgeGroup,
  matchInteriorHeightRules,
  matchInteriorSize,
  matchInteriorsToFramePlacement
} from 'rangeConfig/smastad/filters/selectedMenuProductFilters';
import addProduct from 'rangeConfig/shared/behaviors/addProduct';
import { noProductsWithTypesForSelectedEntity } from 'rangeConfig/shared/rules/noneOptionSelectedRules';
import removeAllInteriorsFromSelectedFrame from 'rangeConfig/shared/behaviors/removeAllInteriorsFromSelectedFrame';
import ChangeChangingTableDeskFunctionality from 'rangeConfig/smastad/widgets/ChangeChangingTableDeskFunctionality';
import DimensionsLabel from 'rangeConfig/shared/widgets/DimensionsLabel';
import removeAccessoriesForSelectedEntity from 'rangeConfig/smastad/behaviors/removeAccessoriesForSelectedEntity';
import { isProductType } from 'rangeConfig/shared/rules/hideMenuRules';
import removeFrameDrawer from 'rangeConfig/smastad/behaviors/removeFrameDrawer';
import removeAllHandlesOnSelectedFrame from 'rangeConfig/shared/behaviors/removeAllHandlesOnSelectedFrame';
import addHandlesToFrontsOnSelectedFrame from 'rangeConfig/smastad/behaviors/addHandlesToFrontsOnSelectedFrame';
import showDoors from 'rangeConfig/shared/behaviors/showDoors';
import addFrontStyleToFrame from 'rangeConfig/shared/behaviors/addFrontStyleToFrame';
import { getQPPImage } from 'products';
import addAccessory from 'rangeConfig/smastad/behaviors/addAccessory';
import addSmastadInteriorSuggestion from 'rangeConfig/smastad/behaviors/addSmastadInteriorSuggestion';
import { noneOptionForInteriorSuggestions } from 'rangeConfig/shared/widgets/interiorSuggestions/noneOptionForInteriorSuggestions';

const handles = {
  id: 'pull-out-frame-handles',
  title: 'storageone.productListHandlesTitle',
  cardsTitle: 'name',
  cardsSubtitle: 'typeName, designText',
  behaviors: {
    productClicked: [addHandlesToFrontsOnSelectedFrame],
    productDropped: [addHandlesToFrontsOnSelectedFrame]
  },
  productTypes: [ProductType.HANDLE, ProductType.KNOB, ProductType.EDGE_HANDLE],
  hidden: (coreApi: CoreApi, selectedEntityId: number) =>
    !isProductType(coreApi, selectedEntityId, ProductType.SMASTAD_PULL_OUT_FRAME_108_ASM) &&
    !isProductType(coreApi, selectedEntityId, ProductType.SMASTAD_PULL_OUT_FRAME_196_ASM),
  filters: [],
  productFilters: [],
  noneOption: {
    behaviors: { removeProducts: [removeAllHandlesOnSelectedFrame] },
    selected: noProductsWithTypesForSelectedEntity([
      ProductType.HANDLE,
      ProductType.KNOB,
      ProductType.EDGE_HANDLE
    ]),
    title: 'storageone.productListNoHandlesOptionTitle'
  }
};

const interiors = {
  id: 'frames-selected-menu-interiors',
  title: 'storageone.productListInteriorsTitle',
  cardsSubtitle: 'typeName, designText',
  productThumbToolTip: 'typeName, designText',
  behaviors: {
    productClicked: [hideDoors, addToTarget],
    productDropped: [hideDoors, addProduct]
  },
  productTypes: [
    ProductType.SMASTAD_SHELF,
    ProductType.SMASTAD_CLOTH_RAIL_40,
    ProductType.SMASTAD_CLOTH_RAIL_ASM,
    ProductType.SMASTAD_WIRE_BASKET_ASM,
    ProductType.SMASTAD_RAIL_STRIP_W_HOOKS_ASM,
    ProductType.SMASTAD_CLOTH_RAIL_55_W_HOOKS_ASM
  ],
  productFilters: [matchInteriorSize, matchInteriorsToFramePlacement],
  noneOption: {
    behaviors: { removeProducts: [removeAllInteriorsFromSelectedFrame] },
    selected: noProductsWithTypesForSelectedEntity([
      ProductType.SMASTAD_SHELF,
      ProductType.SMASTAD_CLOTH_RAIL_40,
      ProductType.SMASTAD_CLOTH_RAIL_ASM,
      ProductType.SMASTAD_WIRE_BASKET_ASM,
      ProductType.SMASTAD_RAIL_STRIP_W_HOOKS_ASM,
      ProductType.SMASTAD_CLOTH_RAIL_55_W_HOOKS_ASM
    ]),
    title: 'storageone.productListNoInteriorsOptionTitle'
  }
};

const interiorSuggestions: SelectedMenuProductList = {
  id: 'frames-selected-menu-interior-suggestions',
  title: 'storageone.selectedMenuInteriorsCombinationsMenuTitle',
  productThumbToolTip: 'name',
  cardsTitle: '',
  cardsSubtitle: '',
  behaviors: {
    productClicked: [hideDoors, addSmastadInteriorSuggestion],
    productDropped: [hideDoors, addSmastadInteriorSuggestion]
  },
  productTypes: [ProductType.SMASTAD_INTERIOR_SUGGESTION],
  productFilters: [matchDimensions, matchAgeGroup, matchInteriorHeightRules],
  noneOption: noneOptionForInteriorSuggestions
};

const frontStyle: SelectedMenuProductList = {
  id: 'frames-selected-menu-front-style',
  title: 'storageone.frontStyleTabTitle',
  cardsTitle: 'name',
  cardsSubtitle: 'typeName, designText',
  productCardMainImage: getQPPImage,
  productCardHoverImage: getQPPImage,
  cluster: ['name', 'design'],
  hidden: (coreApi: CoreApi, selectedEntityId: number) =>
    isProductType(coreApi, selectedEntityId, ProductType.SMASTAD_BENCH) ||
    isProductType(coreApi, selectedEntityId, ProductType.SMASTAD_PULL_OUT_FRAME_108_ASM),
  filters: [],
  behaviors: {
    productDropped: [showDoors, addFrontStyleToFrame],
    productClicked: [showDoors, addFrontStyleToFrame]
  },
  productTypes: [ProductType.SMASTAD_HINGE_DOOR_ASM, ProductType.SMASTAD_DRAWER_ASM],
  productFilters: [matchFrameFrontType],
  sortOrder: [{ path: 'communicatedDimensions.height', reverse: true }]
};

const frameDrawers = {
  id: 'frame-na-selected-menu-drawers',
  title: 'storageone.filterOptionDrawers',
  cardsTitle: 'name',
  cardsSubtitle: 'typeName, designText',
  productThumbToolTip: 'name, designText',
  behaviors: {
    productDropped: [addToTarget],
    productClicked: [addToTarget]
  },
  productTypes: [ProductType.SMASTAD_FRAME_DRAWER_NA],
  filters: [],
  productFilters: [],
  noneOption: {
    behaviors: { removeProducts: [removeFrameDrawer] },
    selected: noProductsWithTypesForSelectedEntity([ProductType.SMASTAD_FRAME_DRAWER_NA]),
    title: 'storageone.productListNoDrawerOptionTitle'
  },
  hidden: hideFrameDrawerWidget
};

const drawerAccessories = {
  id: 'drawer-selected-menu-accessories',
  title: 'storageone.productListAccessoriesTitle',
  cardsTitle: 'name',
  cardsSubtitle: 'designText, communicatedSizeText',
  behaviors: {
    productDropped: [addProduct],
    productClicked: [addAccessory]
  },
  productTypes: [ProductType.SMASTAD_HOOK_CLIP, ProductType.SMASTAD_MIRROR],
  hidden: (coreApi: CoreApi, selectedEntityId: number) =>
    [
      ProductType.SMASTAD_PULL_OUT_FRAME_DRAWER,
      ProductType.SMASTAD_PULL_OUT_FRAME_108,
      ProductType.SMASTAD_PULL_OUT_FRAME_108_ASM,
      ProductType.SMASTAD_PULL_OUT_FRAME_196,
      ProductType.SMASTAD_PULL_OUT_FRAME_196_ASM
    ].every(pt => !isProductType(coreApi, selectedEntityId, pt)),
  filters: [],
  productFilters: [],
  noneOption: {
    behaviors: { removeProducts: [removeAccessoriesForSelectedEntity] },
    selected: noProductsWithTypesForSelectedEntity([
      ProductType.SMASTAD_HOOK_CLIP,
      ProductType.SMASTAD_MIRROR
    ]),
    title: 'storageone.productListNoExteriorAccessoriesOptionTitle'
  }
};

const widgets = {
  desktop: [
    {
      id: 'display-size',
      title: 'storageone.sizeFilterLabel',
      component: DimensionsLabel
    },
    {
      id: 'frames-selected-menu-top-panel',
      component: TopPanelWidgetSelectedMenu,
      title: 'storageone.productListTopPanelsTitle',
      hidden: hideTopPanelsMenu
    },
    {
      id: 'changing-table-desk-functionality',
      component: ChangeChangingTableDeskFunctionality,
      title: 'storageone.function',
      hidden: hideChangingTableFunctionWidget
    },
    frontStyle,
    interiorSuggestions,
    interiors,
    frameDrawers,
    handles,
    drawerAccessories
  ],
  mobile: [
    {
      id: 'frames-selected-menu-top-panel',
      component: TopPanelWidgetSelectedMenu,
      title: 'storageone.productListTopPanelsTitle',
      hidden: hideTopPanelsMenu
    },
    {
      id: 'frames-selected-menu-doors',
      title: 'storageone.productListCabinetsTitle',
      cardsTitle: '',
      cardsSubtitle: '',
      productThumbToolTip: 'name',
      behaviors: {
        productClicked: [replaceProduct],
        productDropped: [replaceProduct]
      },
      noThumbs: () => true,
      productTypes: [ProductType.SMASTAD_FRAME],
      productFilters: [matchDimensions]
    },
    frontStyle,
    interiorSuggestions,
    interiors,
    handles
  ]
};

const selectedMenu: SelectedMenu = {
  menuTitle: 'storageone.productListCustomiseCabinetsTitle',
  behaviors: {
    productDeleted: [deleteSelectedEntity]
  },
  content: widgets[platform]
};

export default selectedMenu;
